import React, { FC, useRef } from "react";
import { Badge, Button, Popover, Row } from "antd";
import { useTranslation } from "react-i18next";
import FilterableItemSwitch from "./FilterableItemSwitch";
import useTableHeaderPopover from "../../userManagement/hooks/useTableHeaderPopover";
import "./TableHeaderFilter.scss";
import { Option } from "../dataRefining/interfaces/filteringData";

/*
 * The `prop` / `value` / `updateFilter` triple is deliberately left as `any`.
 *
 * These props are fed straight from `useTableDataFilter`, whose
 * `handleFilterUpdate` is `(prop: keyof T, value: T[keyof T]) => void` and whose
 * `getValueFromFilterQuery` returns `F[keyof F]` (a union of every filterable
 * field's type). Typing them honestly means making this component — and
 * `FilterableItemSwitch`, `FilterableItem` and `InputFilterItem` — generic over
 * the filtered record, and narrowing `getValueFromFilterQuery` to
 * `<K extends keyof F>(prop: K) => F[K]`. Any attempt short of that only trades
 * `any` for equally unsound casts (e.g. `value as string`), so it is left for a
 * deliberate refactor of the filtering chain rather than guessed at here.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
interface Props {
  prop: any;
  value: any;
  label: string;
  updateFilter: (prop: any, value: any) => void;
  applyFilter: () => void;
  options?: Array<Option>;
}
/* eslint-enable @typescript-eslint/no-explicit-any */

interface PropExtended extends Props {
  setIsOpen: (open: boolean) => void;
  isOpen: boolean;
}

const TableHeaderFilter: FC<Props> = ({
  prop,
  value,
  updateFilter,
  applyFilter,
  label,
  options,
}) => {
  const { handleOpenChange, isOpen } = useTableHeaderPopover(applyFilter);

  const hasFilter = value && value.length;

  return (
    <div
      className="column-header-container"
      onClick={(event) => event.stopPropagation()}
    >
      <Row className="content-align-container">
        <Popover
          placement="bottomRight"
          content={
            <FilterPopoverBody
              setIsOpen={handleOpenChange}
              isOpen={isOpen}
              updateFilter={updateFilter}
              applyFilter={applyFilter}
              value={value}
              label={label}
              prop={prop}
              options={options}
            />
          }
          overlayClassName="filter-popover"
          trigger="click"
          onOpenChange={handleOpenChange}
          open={isOpen}
          destroyTooltipOnHide
        >
          <Badge dot={hasFilter} size="small" color="#38b2ac">
            <i className="bi bi-funnel-fill overview-header__icon" />
          </Badge>
        </Popover>
      </Row>
    </div>
  );
};

const FilterPopoverBody: FC<PropExtended> = ({
  setIsOpen,
  isOpen,
  applyFilter,
  updateFilter,
  value,
  label,
  prop,
  options,
}) => {
  const renderRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation("overview");

  return (
    <div className="filter-popover__container" ref={renderRef}>
      <FilterableItemSwitch
        renderRef={renderRef}
        isOpen={isOpen}
        updateFilter={updateFilter}
        value={value}
        label={label}
        prop={prop}
        options={options}
      />
      <Button
        type="primary"
        onClick={() => {
          applyFilter();
          setIsOpen(false);
        }}
        block
      >
        {t("filters.button")}
      </Button>
    </div>
  );
};

export default TableHeaderFilter;
